import { Copy, FileClock, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { Page } from "~/components/layout/Page";
import { Button, Card } from "~/components/ui";
import { useAuth } from "~/features/authn/context";
import { useGeoItemCreate } from "~/features/geoItems/hooks";
import { useUnsavedChangesWarning } from "~/features/geoItems/hooks/useUnsavedChangesWarning";
import type { GeoItemCreateDraftData } from "~/features/geoItems/types";
import { emptyGeoItemCreateForm } from "~/features/geoItems/types";
import {
  deleteGeoItemCreateDraft,
  type GeoItemCreateDraft,
  type GeoItemCreateDraftScope,
  getGeoItemCreateDraftStorageKey,
  loadGeoItemCreateDrafts,
  saveGeoItemCreateDraft,
} from "~/features/geoItems/utils/geoItemCreateDraft";
import { EditMapToolsProvider } from "~/features/recordGeoItems";
import { v2Route } from "~/v2/routes";
import { GeoItemCreateForm } from "./GeoItemCreateForm";

const getScopeKey = (scope: GeoItemCreateDraftScope | null): string | null =>
  scope ? encodeURIComponent(scope.userId) : null;

/** Recordへ紐づけずにGeoItemを作成する。`/area-input/create` で表示する。 */
export function GeoItemCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;
  const scope = useMemo<GeoItemCreateDraftScope | null>(() => (userId ? { userId } : null), [userId]);
  const scopeKey = getScopeKey(scope);
  const [loadedScopeKey, setLoadedScopeKey] = useState(scopeKey);
  const [drafts, setDrafts] = useState<GeoItemCreateDraft[]>(() => (scope ? loadGeoItemCreateDrafts(scope) : []));
  const [activeDraftId, setActiveDraftId] = useState<string>();
  const [initialDraft, setInitialDraft] = useState<GeoItemCreateDraftData>();
  const [editorRevision, setEditorRevision] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [createdGeoItemId, setCreatedGeoItemId] = useState<string | null>(null);

  useEffect(() => {
    if (scopeKey === loadedScopeKey) return;

    setLoadedScopeKey(scopeKey);
    setDrafts(scope ? loadGeoItemCreateDrafts(scope) : []);
    setActiveDraftId(undefined);
    setInitialDraft(undefined);
    setHasUnsavedChanges(false);
    setEditorRevision((revision) => revision + 1);
  }, [loadedScopeKey, scope, scopeKey]);

  const isDraftScopeReady = Boolean(scope && scopeKey === loadedScopeKey);

  useEffect(() => {
    if (!scope || !isDraftScopeReady) return;

    const storageKey = getGeoItemCreateDraftStorageKey(scope);
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey && !event.key?.startsWith(`${storageKey}:`)) return;
      setDrafts(loadGeoItemCreateDrafts(scope));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [isDraftScopeReady, scope]);

  const confirmNavigation = useUnsavedChangesWarning(
    hasUnsavedChanges && isDraftScopeReady,
    t("feature.geoItems.create.draft.unsavedChanges"),
  );

  const handleCreated = useCallback(
    (geoItemId: string) => {
      if (scope && scopeKey === loadedScopeKey && activeDraftId) {
        deleteGeoItemCreateDraft(scope, activeDraftId);
        setDrafts((current) => current.filter(({ id }) => id !== activeDraftId));
        setActiveDraftId(undefined);
        setInitialDraft(undefined);
      }
      setCreatedGeoItemId(geoItemId);
    },
    [activeDraftId, loadedScopeKey, scope, scopeKey],
  );
  const { submit, processing } = useGeoItemCreate({ onCreated: handleCreated });

  const handleTemporarySave = useCallback(
    (data: GeoItemCreateDraftData): boolean => {
      if (!scope || scopeKey !== loadedScopeKey) return false;

      const savedDraft = saveGeoItemCreateDraft(scope, data, activeDraftId);
      if (!savedDraft) {
        toast.error(t("feature.geoItems.create.draft.temporarySaveFailed"));
        return false;
      }

      setDrafts((current) => [savedDraft, ...current.filter(({ id }) => id !== savedDraft.id)]);
      setActiveDraftId(savedDraft.id);
      setInitialDraft(data);
      setHasUnsavedChanges(false);
      toast.success(t("feature.geoItems.create.draft.temporarySaved"));
      return true;
    },
    [activeDraftId, loadedScopeKey, scope, scopeKey, t],
  );

  const handleResumeDraft = (draft: GeoItemCreateDraft) => {
    confirmNavigation(() => {
      setActiveDraftId(draft.id);
      setInitialDraft(draft.data);
      setHasUnsavedChanges(false);
      setEditorRevision((revision) => revision + 1);
    });
  };

  const handleStartNew = () => {
    confirmNavigation(() => {
      setActiveDraftId(undefined);
      setInitialDraft(undefined);
      setHasUnsavedChanges(false);
      setEditorRevision((revision) => revision + 1);
    });
  };

  const handleDeleteDraft = (draftId: string) => {
    if (!scope || scopeKey !== loadedScopeKey) return;

    const removeDraft = () => {
      deleteGeoItemCreateDraft(scope, draftId);
      setDrafts((current) => current.filter(({ id }) => id !== draftId));
      if (activeDraftId !== draftId) return;
      setActiveDraftId(undefined);
      setInitialDraft(undefined);
      setHasUnsavedChanges(false);
      setEditorRevision((revision) => revision + 1);
    };
    if (activeDraftId === draftId) confirmNavigation(removeDraft);
    else removeDraft();
  };

  const handleCopyId = async () => {
    if (!createdGeoItemId) return;
    try {
      await copyToClipboard(createdGeoItemId);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  return (
    <Page className="space-y-5">
      <h1 className="text-dewey-title capitalize text-fg-1">{t("feature.geoItems.create.title")}</h1>

      {createdGeoItemId && (
        <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle px-4 py-3">
          <span className="text-12-bold text-fg-2">{t("feature.geoItems.create.createdGeoItemId")}</span>
          <span className="text-[13px] tabular-nums text-fg-1">{createdGeoItemId}</span>
          <Button type="button" variant="ghost" size="icon" aria-label={t("ui.copyId")} onClick={handleCopyId}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Link
            to={v2Route.geoItems.detail.$buildPath({ params: { id: createdGeoItemId } })}
            className="text-[13px] text-fg-link hover:text-primary-700"
          >
            {t("ui.detail")}
          </Link>
          <Link to={v2Route.geoItems.$buildPath({})} className="text-[13px] text-fg-link hover:text-primary-700">
            {t("feature.geoItems.list.open")}
          </Link>
        </div>
      )}

      {isDraftScopeReady && drafts.length > 0 && (
        <Card className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <FileClock className="h-4 w-4 text-fg-2" />
                <h2 className="text-[15px] font-semibold text-fg-1">{t("feature.geoItems.create.draft.listTitle")}</h2>
              </div>
              <p className="mt-1 text-[12px] text-fg-2">{t("feature.geoItems.create.draft.listDescription")}</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleStartNew} disabled={processing}>
              <Plus />
              {t("feature.geoItems.create.draft.startNew")}
            </Button>
          </div>
          <ul className="divide-y divide-border-default rounded-[var(--radius-sm)] border border-border-default">
            {drafts.map((draft) => {
              const label = draft.data.form.jaLabel.trim() || draft.data.form.enLabel.trim();
              const isActive = draft.id === activeDraftId;
              return (
                <li key={draft.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium text-fg-1">
                      {label || t("feature.geoItems.create.draft.untitled")}
                      {isActive && (
                        <span className="ml-2 text-[11px] font-normal text-primary-700">
                          {t("feature.geoItems.create.draft.active")}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[11px] text-fg-3">
                      {t("model.common.updatedAt")}: {new Date(draft.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleResumeDraft(draft)}
                      disabled={processing || isActive}
                    >
                      {t("feature.geoItems.create.draft.resume")}
                    </Button>
                    <Button
                      type="button"
                      variant="danger-outline"
                      size="sm"
                      onClick={() => handleDeleteDraft(draft.id)}
                      disabled={processing}
                    >
                      {t("ui.delete")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <EditMapToolsProvider>
        {isDraftScopeReady && (
          <GeoItemCreateForm
            key={`${scopeKey}:${editorRevision}`}
            defaultValues={emptyGeoItemCreateForm}
            initialDraft={initialDraft}
            processing={processing}
            onSubmit={submit}
            onCancel={() => navigate(-1)}
            onDraftChange={(_, isUnsaved) => setHasUnsavedChanges(isUnsaved)}
            onTemporarySave={handleTemporarySave}
          />
        )}
      </EditMapToolsProvider>
    </Page>
  );
}
